import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Info, CreditCard } from 'lucide-react'
import { DropZoneCompact } from '@/components/documentos/DropZoneCompact'
import type { Cuenta } from '@/types/database'
import type { RepartoFifo } from '@/services/aplicacionesService'
import { formatCurrency } from '@/lib/utils'

interface PagoFormFieldsProps {
  fechaPago: string
  onFechaChange: (value: string) => void
  montoFormateado: string
  onMontoChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onMontoBlur: () => void
  esPagoParcial: boolean
  esMontoExcedente: boolean
  repartoPrevisto: RepartoFifo | null
  totalDeudasSeleccionadas: number
  montoNumerico: number
  conceptoEditado: string
  conceptoGenerado: string
  onConceptoChange: (value: string) => void
  cuentaId: string
  onCuentaChange: (value: string) => void
  cuentas: Cuenta[]
  observaciones: string
  onObservacionesChange: (value: string) => void
  archivoComprobante: File | null
  onArchivoChange: (file: File | null) => void
  guardando: boolean
}

export function PagoFormFields({
  fechaPago,
  onFechaChange,
  montoFormateado,
  onMontoChange,
  onMontoBlur,
  esPagoParcial,
  esMontoExcedente,
  repartoPrevisto,
  totalDeudasSeleccionadas,
  montoNumerico,
  conceptoEditado,
  conceptoGenerado,
  onConceptoChange,
  cuentaId,
  onCuentaChange,
  cuentas,
  observaciones,
  onObservacionesChange,
  archivoComprobante,
  onArchivoChange,
  guardando,
}: PagoFormFieldsProps) {
  return (
    <>
      {/* Fecha del pago */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Fecha del pago *</label>
        <Input
          type="date"
          value={fechaPago}
          max={new Date().toISOString().split('T')[0]}
          onChange={(e) => onFechaChange(e.target.value)}
        />
      </div>

      {/* Monto del pago */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Monto del pago *</label>
        <Input
          type="text"
          inputMode="decimal"
          placeholder="0"
          value={montoFormateado}
          onChange={onMontoChange}
          onBlur={onMontoBlur}
        />
        {/* Altura mínima reservada para evitar saltos de layout; el destino del excedente
            puede ocupar dos renglones y no debe quedar cortado. */}
        <div className="min-h-5">
          {esPagoParcial && (
            <p className="text-xs text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />
              Pago parcial: quedará un saldo de {formatCurrency(totalDeudasSeleccionadas - montoNumerico)}
            </p>
          )}
          {/* El excedente ya NO queda a favor por default: se imputa a las deudas abiertas que
              quedaron sin tildar, más viejas primero (ORR-307). El aviso nombra a cuáles va,
              porque el usuario lo lee antes de guardar y es lo que decide si sigue o corrige. */}
          {esMontoExcedente && repartoPrevisto && repartoPrevisto.excedenteImputado.length > 0 && (
            <p className="text-xs text-blue-600 dark:text-blue-400 flex items-start gap-1">
              <Info className="h-3 w-3 shrink-0 mt-0.5" />
              <span>
                El excedente de {formatCurrency(montoNumerico - totalDeudasSeleccionadas)} se
                aplicará a{' '}
                {repartoPrevisto.excedenteImputado
                  // Por concepto Y período: dos deudas del mismo mes son lo normal (honorarios +
                  // inscripción de junio, por ejemplo), y nombrarlas solo por `mes_anio` no dice a
                  // cuál va la plata. Detectado en el smoke del preview del PR #974.
                  .map(e => `${e.deuda.concepto}${e.deuda.mes_anio ? ` ${e.deuda.mes_anio}` : ''} (${formatCurrency(e.monto)})`)
                  .join(', ')}
                {repartoPrevisto.sobrante > 0 &&
                  `, y ${formatCurrency(repartoPrevisto.sobrante)} quedará a favor del cliente`}
              </span>
            </p>
          )}
          {esMontoExcedente && (!repartoPrevisto || repartoPrevisto.excedenteImputado.length === 0) && (
            <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />
              El excedente de {formatCurrency(montoNumerico - totalDeudasSeleccionadas)} quedará a favor del cliente
            </p>
          )}
        </div>
      </div>

      {/* Concepto editable */}
      {conceptoGenerado && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Concepto</label>
          <Input
            value={conceptoEditado}
            onChange={(e) => onConceptoChange(e.target.value)}
            placeholder="Concepto del pago"
          />
        </div>
      )}

      {/* Cuenta (payment method) */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Cuenta *</label>
        <Select value={cuentaId} onValueChange={onCuentaChange}>
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar cuenta" />
          </SelectTrigger>
          <SelectContent>
            {cuentas.map((cuenta) => (
              <SelectItem key={cuenta.id} value={cuenta.id}>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  {cuenta.nombre}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Observaciones */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Observaciones</label>
        <Textarea
          placeholder="Notas adicionales (opcional)"
          rows={2}
          value={observaciones}
          onChange={(e) => onObservacionesChange(e.target.value)}
        />
      </div>

      {/* Receipt attachment (optional) */}
      <div className="space-y-2">
        <label className="text-sm font-medium">
          Comprobante de pago <span className="text-muted-foreground font-normal">(opcional)</span>
        </label>
        <DropZoneCompact
          file={archivoComprobante}
          onFileSelected={onArchivoChange}
          disabled={guardando}
        />
        <p className="text-xs text-muted-foreground">
          Adjunta una imagen o PDF del comprobante de transferencia o depósito
        </p>
      </div>
    </>
  )
}
